import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import {useNavigate} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {AdminCompanyAiPricingDto, changedLine, getAiPricing, setAiPricing} from './ai-pricing.service';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC'});

// SQ-712: AI pricing is off for every company until SavaQuan turns it on
// here, one company at a time. Off, the server never runs the AI for the
// company and its apps show no AI time, price or button; turning it back
// on brings back whatever suggestions it already had. Companies never see
// this switch.
const AiPricingTab: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<AdminCompanyAiPricingDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [busyCompany, setBusyCompany] = useState<number | null>(null);

  const failed = useCallback((context: string, e: unknown) => {
    if (isAuthRedirectError(e)) return;
    logError(context, {}, e as Error);
    enqueueSnackbar((e as Error).message, {variant: 'error', autoHideDuration: 8000});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCompanies(await getAiPricing(navigate));
    } catch (e) {
      failed('AI pricing list failed', e);
    } finally {
      setLoading(false);
    }
  }, [navigate, failed]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (company: AdminCompanyAiPricingDto, enabled: boolean) => {
    setBusyCompany(company.companyId);
    try {
      const updated = await setAiPricing(navigate, company.companyId, enabled);
      setCompanies(current => current && current.map(c => c.companyId === updated.companyId ? updated : c));
      enqueueSnackbar(`AI pricing is ${enabled ? 'on' : 'off'} for ${company.companyName}.`, {variant: 'success'});
    } catch (e) {
      failed('AI pricing change failed', e);
    } finally {
      setBusyCompany(null);
    }
  };

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return (companies ?? []).filter(c => !needle || c.companyName.toLowerCase().includes(needle));
  }, [companies, filter]);

  const onCount = (companies ?? []).filter(c => c.aiPricingEnabled).length;

  return (
    <Box sx={{p: 3}}>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2, maxWidth: 720}}>
        With AI pricing off, a company's records are never sent to the AI, and its apps show no AI time, price or
        &ldquo;Generate AI Price&rdquo; button (Show Comparables still works). Turning it on takes effect on the
        company&rsquo;s next request.
      </Typography>

      <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 2}}>
        <TextField
          label="Company"
          value={filter}
          onChange={event => setFilter(event.target.value)}
          size="small"
          sx={{flex: 1, maxWidth: 420}}
          autoComplete="off"
        />
        <Button variant="outlined" onClick={() => void load()} disabled={loading} startIcon={<SyncIcon/>}>
          Refresh
        </Button>
        {companies && (
          <Typography variant="body2" color="text.secondary" sx={{ml: 1}}>
            On for {onCount} of {companies.length}
          </Typography>
        )}
      </Box>

      {loading && !companies && <CircularProgress sx={{m: 3}}/>}

      {companies && shown.length === 0 && (
        <Typography color="text.secondary">
          {companies.length === 0 ? 'There are no companies yet.' : 'No company matches that.'}
        </Typography>
      )}

      {shown.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="company AI pricing">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>AI pricing</TableCell>
                <TableCell>Last changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(company => {
                const busy = busyCompany === company.companyId;
                return (
                  <TableRow key={company.companyId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{company.companyName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.memberCount} {company.memberCount === 1 ? 'member' : 'members'} · since {day(company.companyCreatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Switch
                          checked={company.aiPricingEnabled}
                          disabled={busy}
                          onChange={(_, checked) => void toggle(company, checked)}
                          inputProps={{'aria-label': `AI pricing for ${company.companyName}`}}
                        />
                        <Typography variant="body2">{company.aiPricingEnabled ? 'On' : 'Off'}</Typography>
                        {busy && <CircularProgress size={14}/>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{changedLine(company)}</Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AiPricingTab;
